/**
 * @file ActivityCalendar.jsx
 * @description A GitHub-style heat map component that visualizes user submission activity
 *              over the past 365 days. Includes a legend and summary statistics.
 *              Adjusts dynamically to provide a horizontal-scrolling grid on mobile.
 * 
 * @module components/ActivityCalendar
 */
import React, { useState, useEffect } from 'react';
import { apiGet } from '../utils/apiUtils';

/* ══════════════════════════════════════════════════════════════
 * HELPERS
 * ══════════════════════════════════════════════════════════════ */

/**
 * Formats a Date object to a local YYYY-MM-DD string, avoiding UTC timezone shifts.
 * 
 * @param {Date} date - The date to format.
 * @returns {string} Formatted date string.
 */
const formatDateToLocalYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/* ══════════════════════════════════════════════════════════════
 * MAIN COMPONENT
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ActivityCalendar
 * @description Renders a 53-week submission heatmap.
 *              Fetches activity data and streak information for a specific user.
 * 
 * @param {Object} props
 * @param {string} props.userId - The ID of the user whose activity to display.
 * @param {string} props.token - Authentication token for API requests.
 */
const ActivityCalendar = ({ userId, token }) => {
    // State: Activity and loading status
    const [activityData, setActivityData] = useState({});
    const [loading, setLoading] = useState(true);

    // State: User activity metrics
    const [stats, setStats] = useState({
        totalSubmissions: 0,
        longestStreak: 0,
        currentStreak: 0,
        activeDays: 0
    });

    /**
     * Logic to generate a 2D array (weeks x days) representing the 365-day grid.
     * Backfills to the nearest Sunday to ensure a clean grid start.
     */
    const generateHeatmapData = () => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = formatDateToLocalYYYYMMDD(today);

        // Go back 364 days to cover exactly 365 days including today
        const startDate = new Date(today);
        startDate.setDate(startDate.getDate() - 364);

        // Align start date to the previous Sunday for a consistent grid layout
        const startDay = startDate.getDay();
        startDate.setDate(startDate.getDate() - startDay);

        const weeks = [];
        let currentDate = new Date(startDate);
        const months = [];
        let lastMonth = -1;

        // Populate the weeks grid
        while (currentDate <= today) {
            const week = [];
            for (let d = 0; d < 7; d++) {
                if (currentDate > today) {
                    week.push(null); // Future days in the final partial week
                } else {
                    const dateStr = formatDateToLocalYYYYMMDD(currentDate);
                    const dayData = activityData[dateStr] || { count: 0 };

                    // Capture month label positions
                    const currentMonth = currentDate.getMonth();
                    if (currentMonth !== lastMonth) {
                        months.push({
                            name: currentDate.toLocaleDateString('en-US', { month: 'short' }),
                            weekIndex: weeks.length
                        });
                        lastMonth = currentMonth;
                    }

                    week.push({
                        date: new Date(currentDate),
                        dateStr,
                        count: dayData.count,
                        isToday: dateStr === todayStr
                    });
                }
                currentDate.setDate(currentDate.getDate() + 1);
            }
            weeks.push(week);
        }

        return { weeks, months };
    };

    /**
     * Data Fetching: Aggregates activity counts and primary streaks.
     */
    const fetchActivityData = async (currentUser) => {
        if (!currentUser?.id) return;
        try {
            setLoading(true);
            const today = new Date();
            const startDate = new Date(today);
            startDate.setDate(startDate.getDate() - 365);
            const endDate = new Date(today);
            endDate.setDate(endDate.getDate() + 1); // Buffer for timezone overlap
            const startDateStr = formatDateToLocalYYYYMMDD(startDate);
            const endDateStr = formatDateToLocalYYYYMMDD(endDate);

            const [activityResponse, streakResponse] = await Promise.all([
                apiGet(`/auth/activity/${currentUser.id}?startDate=${startDateStr}&endDate=${endDateStr}`),
                apiGet(`/auth/submission-streak/${currentUser.id}`)
            ]);

            if (activityResponse.ok) {
                const data = await activityResponse.json();
                setActivityData(data.activityData || {});
                let updatedStats = data.stats || stats;

                if (streakResponse.ok) {
                    const streakData = await streakResponse.json();
                    updatedStats = {
                        ...updatedStats,
                        currentStreak: streakData.currentStreak,
                        longestStreak: streakData.longestStreak
                    };
                }
                setStats(updatedStats);
            }
        } catch (error) {
            console.error('Heatmap data fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    /* ── Effects ── */
    useEffect(() => {
        if (userId) {
            fetchActivityData({ id: userId });
        }
    }, [userId, token]);

    /**
     * Mapping functions for visual intensity
     */
    const getIntensityLevel = (count) => {
        if (count === 0) return 0;
        if (count <= 2) return 1;
        if (count <= 5) return 2;
        if (count <= 10) return 3;
        return 4;
    };

    const getIntensityColor = (level) => {
        const colors = [
            'bg-accent',         // 0 submissions
            'bg-emerald-200',    // low
            'bg-emerald-400',    // medium-low
            'bg-emerald-600',    // medium-high
            'bg-emerald-800'     // high intensity
        ];
        return colors[level] || colors[0];
    };

    /**
     * Generates descriptive hover tooltips.
     */
    const getTooltipContent = (dayData) => {
        const dateStr = dayData.date.toLocaleDateString('en-US', {
            weekday: 'short', year: 'numeric', month: 'short', day: 'numeric'
        });
        if (dayData.count === 0) return `No submissions on ${dateStr}`;
        const s = dayData.count === 1 ? 'submission' : 'submissions';
        return `${dayData.count} ${s} on ${dateStr}`;
    };

    if (loading) return <ActivityCalendarSkeleton />;

    const { weeks, months } = generateHeatmapData();
    const dayLabels = ['', 'Mon', '', 'Wed', '', 'Fri', ''];

    /* ── Render ── */
    return (
        <div>
            {/* Heatmap Grid Section */}
            <div className="overflow-x-auto">
                <div className="inline-flex flex-col gap-0" style={{ minWidth: 'fit-content' }}>
                    {/* ── Month Titles ── */}
                    <div className="flex ml-7 mb-1">
                        <div className="flex w-full">
                            {months.map((m, i) => {
                                const nextWeekIndex = i < months.length - 1 ? months[i + 1].weekIndex : weeks.length;
                                const span = nextWeekIndex - m.weekIndex;
                                return (
                                    <div key={i} className="text-[10px] text-muted-foreground"
                                        style={{ width: `${span * 14}px`, flexShrink: 0 }}>
                                        {span >= 3 ? m.name : ''}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* ── The 53x7 Grid ── */}
                    <div className="flex gap-0">
                        {/* Day Column (Mon/Wed/Fri labels) */}
                        <div className="flex flex-col gap-[2px] mr-1 pt-0">
                            {dayLabels.map((label, i) => (
                                <div key={i} className="h-[12px] w-6 text-[9px] text-muted-foreground flex items-center justify-end pr-1">
                                    {label}
                                </div>
                            ))}
                        </div>

                        {/* Weeks Viewport */}
                        <div className="flex gap-[2px]">
                            {weeks.map((week, wIdx) => (
                                <div key={wIdx} className="flex flex-col gap-[2px]">
                                    {week.map((day, dIdx) => {
                                        if (!day) return <div key={`e-${wIdx}-${dIdx}`} className="w-[12px] h-[12px]" />;
                                        const level = getIntensityLevel(day.count);
                                        return (
                                            <div
                                                key={day.dateStr}
                                                className={`w-[12px] h-[12px] rounded-[2px] cursor-pointer ${getIntensityColor(level)}`}
                                                title={getTooltipContent(day)}
                                            />
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Bottom Section: Legend & Stats ── */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mt-4 pt-3 border-t border-border gap-2">
                {/* Intensity Legend */}
                <div className="flex items-center gap-1">
                    <span className="text-[10px] text-muted-foreground mr-1">Less</span>
                    {[0, 1, 2, 3, 4].map(level => (
                        <div key={level} className={`w-[10px] h-[10px] rounded-[2px] ${getIntensityColor(level)}`} />
                    ))}
                    <span className="text-[10px] text-muted-foreground ml-1">More</span>
                </div>

                {/* Primary Stats Summary (Desktop only for full row) */}
                <div className="flex items-center gap-3 flex-wrap hidden md:flex">
                    <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-foreground">{stats.totalSubmissions}</span>
                        <span className="text-xs text-muted-foreground">Total Submissions</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-foreground">{stats.activeDays}</span>
                        <span className="text-xs text-muted-foreground">Active Days</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-foreground">{stats.currentStreak}</span>
                        <span className="text-xs text-muted-foreground">Current Streak</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <span className="text-sm font-bold text-foreground">{stats.longestStreak}</span>
                        <span className="text-xs text-muted-foreground">Longest Streak</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ══════════════════════════════════════════════════════════════
 * SKELETON
 * ══════════════════════════════════════════════════════════════ */

/**
 * @component ActivityCalendarSkeleton
 * @description Loading placeholder for the calendar.
 */
const ActivityCalendarSkeleton = () => (
    <div className="animate-pulse">
        <div className="h-[100px] w-full bg-gray-100 rounded mb-3" />
        <div className="flex justify-between">
            <div className="h-3 w-24 bg-gray-100 rounded" />
            <div className="h-3 w-32 bg-gray-100 rounded" />
        </div>
    </div>
);

export default ActivityCalendar;